import { useMemo, useState } from 'react';
import { loadDemoContract } from '../data/loadDemoContract';
import { calculateScenario, createDefaultScenario } from '../domain/equityEngine';
import { usePersistentScenario } from '../hooks/usePersistentScenario';
import { EquityJourneyMode } from './modes/EquityJourneyMode';

const RANDOM_AS_OF_DATES = ['2026-06-20', '2026-12-20', '2027-06-20', '2028-06-20'];
const RANDOM_EVENTS = [
  'none',
  'ordinary_termination',
  'major_breach',
  'confidentiality_breach',
] as const;
export function App() {
  const contract = useMemo(() => loadDemoContract(), []);
  const defaultScenario = useMemo(() => createDefaultScenario(contract), [contract]);
  const { scenario, setScenario, resetScenario } = usePersistentScenario(defaultScenario);
  const calculation = useMemo(() => calculateScenario(scenario), [scenario]);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState('M2');

  const selectedCalculation =
    calculation.milestones.find((milestone) => milestone.milestone.id === selectedMilestoneId) ??
    calculation.milestones[0];

  function handleExercise(milestoneId: string) {
    const target = calculation.milestones.find((milestone) => milestone.milestone.id === milestoneId);
    if (!target) {
      return;
    }

    setScenario((current) => ({
      ...current,
      exercisedBpsByMilestone: {
        ...current.exercisedBpsByMilestone,
        [milestoneId]: target.timeVestedBps,
      },
    }));
  }

  function handleRandomScenario() {
    const seedDate = RANDOM_AS_OF_DATES[Math.floor(Math.random() * RANDOM_AS_OF_DATES.length)];
    const event = RANDOM_EVENTS[Math.floor(Math.random() * RANDOM_EVENTS.length)];
    const conflictCleared = Math.random() > 0.2;
    const achievedCount = Math.ceil(Math.random() * defaultScenario.milestones.length);
    const randomized = structuredClone(defaultScenario);

    randomized.asOfDate = seedDate;
    randomized.conflictCleared = conflictCleared;
    randomized.conflictClearedAt = conflictCleared ? '2026-06-10' : randomized.conflictClearedAt;
    randomized.agreementTerminated = event === 'ordinary_termination';
    randomized.terminationDate = randomized.agreementTerminated ? seedDate : null;
    randomized.serviceActive = event !== 'ordinary_termination';
    randomized.adverseEventReason = event;
    randomized.exercisedBpsByMilestone = {};
    randomized.milestones = randomized.milestones.map((milestone, index) => ({
      ...milestone,
      achieved: conflictCleared && index < achievedCount,
      achievedAt:
        conflictCleared && index < achievedCount
          ? ['2026-06-10', '2026-06-14', '2026-08-20', '2026-10-20', '2026-12-20', '2027-01-20'][
              index
            ] ?? null
          : null,
    }));

    setScenario(randomized);
    setSelectedMilestoneId('M2');
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <div>
          <span className="eyebrow">协议推演 / 法律设计</span>
          <h1>{contract.name}</h1>
          <p>{contract.subtitle}</p>
        </div>
        <div className="header-meta">
          <span>{contract.demoEntityName}</span>
          <strong>截至 {calculation.scenario.asOfDate}</strong>
          <small>{contract.disclaimer}</small>
        </div>
      </header>

      <main>
        <EquityJourneyMode
          contract={contract}
          calculation={calculation}
          selectedCalculation={selectedCalculation}
          selectedMilestoneId={selectedMilestoneId}
          onSelectMilestone={setSelectedMilestoneId}
          onScenarioChange={setScenario}
          onReset={resetScenario}
          onRandom={handleRandomScenario}
          onExercise={handleExercise}
        />
      </main>
    </div>
  );
}
